import React, { createContext, useContext, useMemo } from "react";

export interface BulkSelectionContextValue {
  selectedKeys: Set<string>;
  toggle: (id: string, nextSelected: boolean) => void;
}

const BulkSelectionContext = createContext<BulkSelectionContextValue | null>(
  null,
);

/**
 * Context that lets message-list cards read their bulk-select state directly,
 * bypassing the prop chain.
 *
 * React-spectrum's `ListBox`/`CollectionBuilder` caches the Node (and its
 * rendered React element) by item reference (see
 * `@react-stately/collections/CollectionBuilder.build` - `this.cache`). That
 * means when the parent re-renders with a new `bulkSelectedKeys` Set, the card
 * it re-renders as a child of the `<Item>` is the stale cached element and the
 * new `isBulkSelected` prop never reaches the checkbox. Reading selection
 * state through a Context `Provider` makes React propagate updates to every
 * consumer regardless of the cached Node element, so the checkbox always
 * reflects the latest state.
 */
export const BulkSelectionProvider: React.FC<{
  selectedKeys: Set<string>;
  toggle: (id: string, nextSelected: boolean) => void;
  children: React.ReactNode;
}> = ({ selectedKeys, toggle, children }) => {
  const value = useMemo<BulkSelectionContextValue>(
    () => ({ selectedKeys, toggle }),
    [selectedKeys, toggle],
  );
  return (
    <BulkSelectionContext.Provider value={value}>
      {children}
    </BulkSelectionContext.Provider>
  );
};

export function useBulkSelectionContext(): BulkSelectionContextValue | null {
  return useContext(BulkSelectionContext);
}

export function useBulkSelectionState(rowId: string): {
  isEnabled: boolean;
  isSelected: boolean;
  toggle: (nextSelected: boolean) => void;
} {
  const ctx = useContext(BulkSelectionContext);
  if (!ctx || !rowId) {
    return {
      isEnabled: false,
      isSelected: false,
      toggle: () => undefined,
    };
  }
  return {
    isEnabled: true,
    isSelected: ctx.selectedKeys.has(rowId),
    toggle: (nextSelected) => ctx.toggle(rowId, nextSelected),
  };
}

export default BulkSelectionContext;
